import type Phaser from "phaser";
import { EntityKind, ICON_ART_SIZE, SIXTEENTHS, iconCell, type EntityState, type ItemRarity, type Rules } from "odyssey-shared";
import { ICON_TEXTURE, iconFrame, iconTexture } from "../icons/atlas-texture.ts";
import type { IconSource } from "../icons/page-icons.ts";
import { paintTexture } from "../world/projectile-view.ts";
import { groundAlpha, groundItemOf, groundLook, type GroundLook } from "./find.ts";
import type { GroundMarks } from "./marks.ts";
import { GroundLandings, groundShadowShare } from "./motion.ts";
import { outlineTexture } from "./outline.ts";
import { groundReadingOf } from "./words.ts";

/**
 * Ground items on the page (P2.8-T65, ADR-091's amendment for P2.8-T13, decisions 3 and 6): each entity of kind 10 in
 * the client's view drawn as its item's icon from the page's icon atlas (`icons/atlas-texture.ts`) lying at its point,
 * half a tile across, on a soft shadow, in one of three looks: another character's item while owned, a little dimmed
 * on a soft dark outline of its own shape (`outline.ts`, P2.8-T74), so it still reads on grass; the page's own item
 * while owned, at full strength on a light brass ring (`GroundMarks`); and an item anyone may take, at full strength.
 * The game design draws no ground item, so this look is the page's own, listed for the author. Every item floats, and
 * one that lands while the page watches hops first (`motion.ts`): the icon and its outline rise by whole world pixels
 * and the shadow narrows, the ring staying on the ground; under reduced motion every item lies still.
 *
 * Every image lies at the ground frames' depth (`GROUND_ROW`): over the band's flat layers, under every character and
 * every prop that stands, so an item never hides a player's feet. The images are pooled and placed only when a record
 * of kind 10 appears, moves, changes state or leaves, or a mark changes, which only an applied snapshot or a
 * `lootGained` does; a render frame only moves the icons, outlines and shadows already placed, and allocates nothing. An appearance the page's rules lack (a page on other rules than
 * the server's) is drawn as nothing and said once, and so is an item whose icon the atlas lacks.
 */

/**
 * How many tiles across an item's icon is drawn.
 */
export const GROUND_ITEM_TILES = 0.5;

/**
 * The sort row the icons take: a quarter row above the band's floor, over the shadows and rings at the ground frames'
 * row (`GROUND_ROW`, half a row above it) and under row 0, the smallest row anything that stands sorts by.
 */
export const GROUND_ITEM_ROW = -0.25;

/**
 * How strong an item's shadow is drawn.
 */
const SHADOW_ALPHA = 0.3;

/**
 * The shadow's and the ring's size in the icon's pixels, and how far below the icon's middle they lie.
 */
const SHADOW_WIDTH = 22;
const SHADOW_HEIGHT = 8;
const SHADOW_DROP = 12;

/**
 * The brass the page's own items are ringed in: the design's `--ot-brass-100`, the lightest, which holds on grass.
 */
const OWN_RING = "#f2d898";

/**
 * How thick the ring is in the icon's pixels: two, one world pixel at the icon's half scale.
 */
const RING_WIDTH = 2;

/**
 * The textures of the shadow and the ring.
 */
const SHADOW_KEY = "ground:shadow";
const RING_KEY = "ground:ring";

/**
 * One drawn ground item, as the report lists it and its label reads it (P2.8-T76): its handle, its item, its quantity
 * from its record (P2.8-T85), the label's name (the item's, with "x" and the count above 1) and the rarity by the
 * page's rules, its look, its point in world pixels, and how many world pixels it is drawn above
 * that point, which each render frame keeps current.
 */
export interface GroundReading {
  readonly handle: number;
  readonly item: string;
  readonly quantity: number;
  readonly name: string;
  readonly rarity: ItemRarity;
  readonly look: GroundLook;
  readonly x: number;
  readonly y: number;
  lift: number;
}

/**
 * One pooled ground item's images.
 */
interface Drawn {
  readonly shadow: Phaser.GameObjects.Image;
  readonly ring: Phaser.GameObjects.Image;
  readonly outline: Phaser.GameObjects.Image;
  readonly icon: Phaser.GameObjects.Image;
  handle: number;
  y: number;
  lift: number;
  reading: GroundReading | null;
}

/**
 * Whether an ellipse of a size holds a pixel's middle.
 *
 * @param x The pixel's column.
 * @param y The pixel's row.
 * @param width The ellipse's width.
 * @param height Its height.
 * @returns True inside.
 */
function inEllipse(x: number, y: number, width: number, height: number): boolean {
  return ((x + 0.5 - width / 2) / (width / 2)) ** 2 + ((y + 0.5 - height / 2) / (height / 2)) ** 2 <= 1;
}

/**
 * The ground items the page draws.
 */
export class GroundView {
  private readonly pool: Drawn[] = [];
  private readonly said = new Set<string>();
  private texture: Phaser.Textures.Texture | null = null;
  private signature = "";
  private shown = 0;
  private readonly scale: number;
  private readonly unsubscribe: () => void;
  private readonly landings = new GroundLandings();

  /**
   * The items drawn, as the last `sync` placed them, for the report.
   */
  public readings: readonly GroundReading[] = [];

  /**
   * The handles the last `sync` saw land (P2.8-T77), the first `landedCount` of them; the array grows to the most items
   * that ever landed in one drawing and is reused after.
   */
  public readonly landed: number[] = [];

  /**
   * How many of `landed` the last `sync` saw land: 0 after a call that placed nothing again.
   */
  public landedCount = 0;

  /**
   * @param scene The scene the items draw in.
   * @param icons The page's icons, whose atlas the items are drawn from.
   * @param rules The page's rules, which name the item an appearance is.
   * @param tileSize The world's tile size in pixels.
   * @param shadowDepth The depth of the shadows and rings: the ground frames'.
   * @param iconDepth The depth of the icons: over the shadows and still under every sort row.
   * @param reduced Whether the game's reduced motion is on now, read each frame.
   * @param say Where a line goes; the console's warnings unless a test hands in another.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly icons: IconSource,
    private readonly rules: Rules,
    private readonly tileSize: number,
    private readonly shadowDepth: number,
    private readonly iconDepth: number,
    private readonly reduced: () => boolean = () => false,
    private readonly say: (line: string) => void = (line) => console.warn(line),
  ) {
    this.scale = (tileSize * GROUND_ITEM_TILES) / ICON_ART_SIZE;
    paintTexture(scene.textures, SHADOW_KEY, SHADOW_WIDTH, SHADOW_HEIGHT, "#000000", (x, y) => (inEllipse(x, y, SHADOW_WIDTH, SHADOW_HEIGHT) ? 1 : 0));
    paintTexture(scene.textures, RING_KEY, SHADOW_WIDTH, SHADOW_HEIGHT, OWN_RING, (x, y) => (inEllipse(x, y, SHADOW_WIDTH, SHADOW_HEIGHT) && !inEllipse(x - RING_WIDTH, y - RING_WIDTH, SHADOW_WIDTH - RING_WIDTH * 2, SHADOW_HEIGHT - RING_WIDTH * 2) ? 1 : 0));
    this.unsubscribe = icons.subscribe(() => {
      this.signature = "";
    });
  }

  /**
   * Half an item's drawn square in sixteenths, the box a click picks it up within.
   *
   * @returns The half side.
   */
  public get half(): number {
    return (GROUND_ITEM_TILES * SIXTEENTHS) / 2;
  }

  /**
   * Says a line once for the life of the page.
   *
   * @param topic What the line is about.
   * @param line The line.
   */
  private once(topic: string, line: string): void {
    if (!this.said.has(topic)) {
      this.said.add(topic);
      this.say(line);
    }
  }

  /**
   * The pooled images at an index, made the first time it is needed.
   *
   * @param index The index.
   * @returns The images.
   */
  private drawnAt(index: number): Drawn {
    let drawn = this.pool[index];
    if (drawn === undefined) {
      const scene = this.scene;
      drawn = {
        shadow: scene.add.image(0, 0, SHADOW_KEY).setAlpha(SHADOW_ALPHA).setDepth(this.shadowDepth).setScale(this.scale).setVisible(false),
        ring: scene.add.image(0, 0, RING_KEY).setDepth(this.shadowDepth).setScale(this.scale).setVisible(false),
        outline: scene.add.image(0, 0, "__DEFAULT").setDepth(this.shadowDepth).setScale(this.scale).setVisible(false),
        icon: scene.add.image(0, 0, "__DEFAULT").setDepth(this.iconDepth).setScale(this.scale).setVisible(false),
        handle: -1,
        y: 0,
        lift: -1,
        reading: null,
      };
      this.pool.push(drawn);
    }
    return drawn;
  }

  /**
   * The frame an item's icon draws, or null while the atlas is not a texture, for an item with no icon, or for a key
   * the pointer lacks, the last two said once.
   *
   * @param item The item's rules id.
   * @param key The item's icon key.
   * @returns The frame's name.
   */
  private frameOf(item: string, key: string | null | undefined): string | null {
    const atlas = this.icons.getSnapshot().atlas;
    this.texture ??= iconTexture(this.scene.textures, atlas);
    if (this.texture === null || atlas.status !== "ready") {
      return null;
    }
    if (key === null || key === undefined) {
      this.once(`icon ${item}`, `item ${item} has no icon; its ground item is not drawn`);
      return null;
    }
    const frame = iconFrame(this.texture, key, () => iconCell(atlas.manifest, key));
    if (frame === null) {
      this.once(`key ${key}`, `the icon pointer has no ${key} (item ${item}); its ground item is not drawn`);
    }
    return frame;
  }

  /**
   * The outline texture of an icon drawn as another character's owned item, made the first time it is asked for, or
   * null while the atlas has no image the page decoded itself or the pointer lacks the key.
   *
   * @param key The item's icon key, which `frameOf` has already found in the atlas.
   * @returns The texture key.
   */
  private outlineOf(key: string): string | null {
    const atlas = this.icons.getSnapshot().atlas;
    if (atlas.status !== "ready" || atlas.image.element === undefined) {
      return null;
    }
    const place = iconCell(atlas.manifest, key);
    return place === null ? null : outlineTexture(this.scene.textures, atlas.image.element, key, place);
  }

  /**
   * Draws the ground items in view at their points in their looks, rebuilt only when a record of kind 10, a mark or
   * the atlas changed since the last call, and lists the items it saw land (`landed`).
   *
   * @param entities The client's view.
   * @param marks The page's own items.
   * @returns Whether the items were placed again.
   */
  public sync(entities: ReadonlyMap<number, EntityState>, marks: GroundMarks): boolean {
    this.landedCount = 0;
    this.landings.begin(this.scene.game.loop.time);
    let signature = "";
    for (const state of entities.values()) {
      if (state.kind === EntityKind.GroundItem) {
        signature += `${state.handle}:${state.appearance}:${state.anim}:${state.x}:${state.y}:${marks.owns(state) ? 1 : 0};`;
      }
    }
    if (signature === this.signature) {
      return false;
    }
    this.signature = signature;
    const now = this.scene.game.loop.time;
    const present = new Set<number>();
    const perSixteenth = this.tileSize / SIXTEENTHS;
    const readings: GroundReading[] = [];
    let index = 0;
    for (const state of entities.values()) {
      if (state.kind !== EntityKind.GroundItem) {
        continue;
      }
      present.add(state.handle);
      const item = groundItemOf(this.rules, state.appearance);
      if (item === null) {
        this.once(`appearance ${state.appearance}`, `ground item appearance ${state.appearance} is not in this page's rules; it is not drawn`);
        continue;
      }
      const frame = this.frameOf(item.id, item.icon);
      if (frame === null) {
        continue;
      }
      const look = groundLook(state, marks.owns(state));
      const outline = look === "owned" ? this.outlineOf(item.icon as string) : null;
      const x = state.x * perSixteenth;
      const y = state.y * perSixteenth;
      const drawn = this.drawnAt(index);
      index += 1;
      const drop = SHADOW_DROP * this.scale;
      drawn.shadow.setPosition(x, y + drop).setVisible(true);
      drawn.ring.setPosition(x, y + drop).setVisible(look === "own");
      if (outline === null) {
        drawn.outline.setVisible(false);
      } else {
        drawn.outline.setTexture(outline).setPosition(x, y).setVisible(true);
      }
      drawn.icon.setTexture(ICON_TEXTURE, frame).setPosition(x, y).setAlpha(groundAlpha(look)).setVisible(true);
      if (this.landings.note(state.handle, `${state.appearance}:${state.x}:${state.y}`, now, look === "public")) {
        this.landed[this.landedCount] = state.handle;
        this.landedCount += 1;
      }
      const reading = groundReadingOf(state, item, look, x, y);
      drawn.handle = state.handle;
      drawn.y = y;
      drawn.lift = -1;
      drawn.reading = reading;
      this.lift(drawn, now);
      readings.push(reading);
    }
    for (let i = index; i < this.shown; i += 1) {
      const drawn = this.pool[i] as Drawn;
      drawn.shadow.setVisible(false);
      drawn.ring.setVisible(false);
      drawn.outline.setVisible(false);
      drawn.icon.setVisible(false);
      drawn.reading = null;
    }
    this.shown = index;
    this.readings = readings;
    this.landings.keep(present, now);
    return true;
  }

  /**
   * Starts the landings' settling again, as the character jumps within the session (P2.8-T77).
   */
  public resettle(): void {
    this.landings.settle(this.scene.game.loop.time);
  }

  /**
   * Moves the items drawn to their lift at a moment: run each render frame. An item whose lift has not changed since
   * the last frame is not touched.
   *
   * @param now The game loop's time in milliseconds.
   */
  public frame(now: number): void {
    for (let i = 0; i < this.shown; i += 1) {
      this.lift(this.pool[i] as Drawn, now);
    }
  }

  /**
   * Raises one drawn item's icon and outline by its lift at a moment and narrows its shadow, or lays it still under
   * reduced motion.
   *
   * @param drawn The item's images.
   * @param now The game loop's time in milliseconds.
   */
  private lift(drawn: Drawn, now: number): void {
    const lift = this.reduced() ? 0 : this.landings.liftOf(drawn.handle, now);
    if (lift === drawn.lift) {
      return;
    }
    drawn.lift = lift;
    if (drawn.reading !== null) {
      drawn.reading.lift = lift;
    }
    drawn.icon.setY(drawn.y - lift);
    drawn.outline.setY(drawn.y - lift);
    drawn.shadow.setScale(this.scale * groundShadowShare(lift), this.scale);
  }

  /**
   * Hides every item drawn, as a session ends.
   */
  public clear(): void {
    for (let i = 0; i < this.shown; i += 1) {
      const drawn = this.pool[i] as Drawn;
      drawn.shadow.setVisible(false);
      drawn.ring.setVisible(false);
      drawn.outline.setVisible(false);
      drawn.icon.setVisible(false);
      drawn.reading = null;
    }
    this.shown = 0;
    this.signature = "";
    this.readings = [];
    this.landedCount = 0;
    this.landings.clear();
  }

  /**
   * Destroys the pooled images and stops following the page's icons, as the scene shuts down.
   */
  public destroy(): void {
    this.unsubscribe();
    for (const drawn of this.pool) {
      drawn.shadow.destroy();
      drawn.ring.destroy();
      drawn.outline.destroy();
      drawn.icon.destroy();
    }
    this.pool.length = 0;
    this.shown = 0;
  }
}
